import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core"
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable
} from "@dnd-kit/sortable"
import {
  DownloadOutlined,
  FullscreenOutlined,
  ReloadOutlined
} from "@ant-design/icons"
import { CSS } from "@dnd-kit/utilities"
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Modal,
  Result,
  Row,
  Skeleton,
  Space,
  Typography,
  message
} from "antd"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { UNSAFE_DataRouterContext, useSearchParams } from "react-router-dom"

import {
  DEFAULT_METRIC_ORDER,
  homeCardStyle,
  homeColor
} from "../../home-theme.ts"
import { HomeMetricTile, HomeMetricTileSkeleton } from "../HomeMetricTile.tsx"
import { HomeSectionHeader } from "../HomeSectionHeader.tsx"
import { ChartNoAxesCombinedIcon, ChartPieIcon } from "../home-icons.tsx"

import { ActionTooltip } from "../../../../components/ActionTooltip.tsx"
import { WidgetExpandModal } from "../../../../components/widgets/WidgetExpandModal"
import { WidgetRenderer } from "../../../../components/widgets/WidgetRenderer"
import type {
  Capability,
  WidgetInstance,
  WidgetPayload,
  WidgetProps,
  WidgetType
} from "../../../../components/widgets/types"
import { exportWidgetCsv } from "../../../../components/widgets/widgetCsv"
import { getUIPreferences } from "../../../User/services"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch"
import { useAppSelector } from "../../../../hooks/use-app-selector"
import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback"

import { useProductTour } from "../../../Guide/tour/ProductTourContext.tsx"
import { useGlobalFilterOptions } from "../../hooks/useGlobalFilterOptions"
import { resolveDashboardWidget } from "../../api/services"
import {
  buildFilterParams,
  createEmptyFilters,
  hasActiveFilters,
  parseActiveViewGlobalFilters,
  parseGlobalFilters
} from "../../redux/globalFilters"
import {
  fetchNba,
  setConfigKey,
  setGlobalFilters,
  setTeamScope
} from "../../redux/portfolio.slice"
import {
  dropResolvedWidget,
  fetchCatalog,
  fetchWidgets,
  isWidgetSnapshotFresh,
  resolveWidget
} from "../../redux/widgets.slice"
import type { FilterLabels, GlobalFilters } from "../../redux/portfolio.types"
import type { CatalogWidgetSpec } from "../../redux/widgets.types"
import {
  ENGAGEMENT_INSTANCE,
  ENGAGEMENT_METRIC,
  prefixFilterParams,
  useEngagementWidgetHost
} from "../../hooks/useEngagementWidgetHost"
import { AddWidgetsModal, type CatalogEntry } from "./AddWidgetsModal"
import { EditLayoutBar } from "./EditLayoutBar"
import { RouteDraftBlocker, UnsavedDraftDialog } from "./LayoutDraftGuard"
import { ExportButtons } from "./ExportButtons"
import { FilterBar } from "./FilterBar"
import { KPI_CARD_COL_WIDE } from "./kpiGrid"
import { PortfolioDashboard } from "./PortfolioDashboard"
import { feedWidget } from "./portfolioWidgetAdapter"
import { SavedViewBar } from "./SavedViewBar"
import { WidgetDragHandle, WidgetRemoveButton } from "./WidgetEditChrome"
import { TimeWindowControl } from "./TimeWindowControl"
import { useDashboardLayout } from "./useDashboardLayout"
import {
  BUILT_IN_SLUGS,
  CASH_FLOW_SLUG,
  NBA_FEED_SLUG,
  PROJECT_STATUS_SLUG,
  type AdaptedWidget,
  adaptResolved,
  catalogBoardSpecs,
  catalogFeedAlertSpecs,
  catalogKpiSpecs,
  catalogSnapshotSpecs,
  currencyFromWidgets,
  fixedWidgetInstances,
  fixedWidgetInstancesFor,
  indexCatalog,
  isBoardSlug,
  boardChartRows,
  isFeedAlertSlug,
  isKpiSlug,
  isSnapshotSlug,
  liftTileToInstance,
  metricFromSlug,
  resolvedSlug,
  responseToInstances,
  slugFor,
  specToInstance,
  typeFromSlug,
  valueKey
} from "./widgetsResponseAdapter"

const NBA_POLL_INTERVAL = 5 * 60 * 1000
const CATALOG_MAX_RETRIES = 3
const CATALOG_RETRY_BASE_MS = 300

const CapabilityActions = ({
  capabilities,
  onRefresh,
  onExpand,
  onExport
}: {
  capabilities?: Capability[]
  onRefresh: () => void
  onExpand?: () => void
  onExport?: () => void
}) => {
  const { t } = useTranslation()
  const caps = capabilities ?? []
  const showRefresh = caps.includes("refresh")
  const showExpand = caps.includes("expand") && Boolean(onExpand)
  const showExport = caps.includes("export") && Boolean(onExport)
  if (!showRefresh && !showExpand && !showExport) return null
  return (
    <Space size={4}>
      {showRefresh && (
        <ActionTooltip title={t("widget.action.refresh", "Refresh")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.refresh", "Refresh")}
            data-testid="widget-refresh"
            icon={<ReloadOutlined />}
            onClick={onRefresh}
          />
        </ActionTooltip>
      )}
      {showExpand && (
        <ActionTooltip title={t("widget.action.expand", "Expand")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.expand", "Expand")}
            data-testid="widget-expand"
            icon={<FullscreenOutlined />}
            onClick={onExpand}
          />
        </ActionTooltip>
      )}
      {showExport && (
        <ActionTooltip title={t("widget.action.export", "Export")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.export", "Export")}
            data-testid="widget-export"
            icon={<DownloadOutlined />}
            onClick={onExport}
          />
        </ActionTooltip>
      )}
    </Space>
  )
}

interface KpiCardShellProps {
  slug: string
  adapted?: AdaptedWidget
  currency?: string
  onHide?: (slug: string) => void
}

const KpiCardBody = ({
  adapted,
  currency
}: {
  adapted?: AdaptedWidget
  currency?: string
}) => {
  const tile = (adapted?.payload as WidgetPayload | undefined)?.tile
  if (!adapted || !tile) return <HomeMetricTileSkeleton />
  return (
    <HomeMetricTile
      tile={tile}
      currency={(adapted.payload as WidgetPayload).currency ?? currency}
    />
  )
}

const SortableKpiCard = ({
  slug,
  adapted,
  currency,
  onHide
}: KpiCardShellProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: slug })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    minWidth: 0,
    position: "relative" as const,
    background: homeColor.surface
  }
  // The listeners sit on the handle, not the whole cell: a cell-wide drag
  // target swallows the press that opens Remove, and leaves a keyboard user
  // with no way to reorder at all.
  return (
    <div ref={setNodeRef} style={style} data-home-metric-cell={slug}>
      <div
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          display: "flex",
          alignItems: "center",
          gap: 2,
          zIndex: 1
        }}
      >
        <WidgetDragHandle
          title={adapted?.widget.title}
          dragProps={{ ...attributes, ...listeners }}
        />
        {onHide && (
          <WidgetRemoveButton
            title={adapted?.widget.title}
            onRemove={() => onHide(slug)}
          />
        )}
      </div>
      <KpiCardBody adapted={adapted} currency={currency} />
    </div>
  )
}

const StaticKpiCard = ({ slug, adapted, currency }: KpiCardShellProps) => (
  <div key={slug} style={{ minWidth: 0 }}>
    <KpiCardBody adapted={adapted} currency={currency} />
  </div>
)

interface BoardWidgetCardProps {
  slug: string
  adapted?: AdaptedWidget
  currency?: string
  isEditing?: boolean
  refreshing?: boolean
  onHide?: (slug: string) => void
  onRefresh?: () => void
  onExpand?: () => void
  onEngagementDrill?: WidgetProps["onEngagementDrill"]
  loadEngagementRange?: WidgetProps["loadEngagementRange"]
}

const BoardWidgetCard = ({
  slug,
  adapted,
  currency,
  onEngagementDrill,
  loadEngagementRange,
  isEditing,
  refreshing,
  onHide,
  onRefresh,
  onExpand
}: BoardWidgetCardProps) => {
  const editChrome =
    isEditing && onHide ? (
      <WidgetRemoveButton
        title={adapted?.widget.title}
        onRemove={() => onHide(slug)}
      />
    ) : undefined
  const body = adapted ? (
    <WidgetRenderer
      widget={adapted.widget}
      payload={adapted.payload as WidgetPayload}
      state={refreshing ? "loading" : "ready"}
      currency={currency}
      onRefresh={onRefresh}
      onExpand={onExpand}
      onEngagementDrill={onEngagementDrill}
      loadEngagementRange={loadEngagementRange}
      editChrome={editChrome}
    />
  ) : (
    <WidgetRenderer
      widget={
        { id: slug, type: typeFromSlug(slug) as WidgetType } as WidgetInstance
      }
      payload={null}
      state="loading"
      editChrome={editChrome}
    />
  )
  // Tables and lists draw their own header; a chart on the board needs the
  // same titled card the fixed Cash Flow chart sits in.
  const framed =
    typeFromSlug(slug) === "chart" ? (
      <Card
        title={adapted?.widget.title}
        size="small"
        extra={
          <Flex align="center" gap={4}>
            {onRefresh ? (
              <CapabilityActions
                capabilities={adapted?.widget.capabilities}
                onRefresh={onRefresh}
                onExpand={onExpand}
                onExport={
                  // The table exports its own rows, in the grouping on screen.
                  adapted?.payload.chart?.engagement
                    ? undefined
                    : () =>
                        exportWidgetCsv({
                          title: adapted?.widget.title ?? slug,
                          payload: adapted?.payload
                        })
                }
              />
            ) : null}
            {editChrome}
          </Flex>
        }
      >
        {body}
      </Card>
    ) : (
      body
    )
  // A plain block, not `<Col xs={24}>` in a gutter `<Row>`: the gutter's
  // -8px side margins made every board row 16px wider than the column it sits
  // in, which is horizontal overflow inside a `min-w-0` grid child. The span
  // was always the full 24 anyway, so the Row only ever supplied the vertical
  // gap a flex column gives with no negative margins.
  return (
    <div key={slug} data-board-tile={slug} style={{ minWidth: 0 }}>
      {framed}
    </div>
  )
}

interface WidgetsDashboardProps {
  /**
   * Lets the host place the My View / Edit Layout pair and Export itself —
   * Home puts them inside its welcome band. Omitted (Company HQ, the
   * Portfolio fallback) keeps the toolbar row this component draws.
   */
  renderHeader?: (
    viewControls: React.ReactNode,
    exportControl: React.ReactNode
  ) => React.ReactNode
  /**
   * Home's below-the-fold sections: `mainColumnFooter` continues the main
   * column under the charts (the shortcut row, then the recent list),
   * `sideColumnFooter` sits in the right rail under Next Best Actions.
   * Omitted (Company HQ, the Portfolio fallback) renders nothing there — and
   * a rail with no content at all is not rendered, so its track is never
   * reserved.
   */
  mainColumnFooter?: React.ReactNode
  sideColumnFooter?: React.ReactNode
  // Which `UIPreferences` key this instance's layout persists under — see
  // `useDashboardLayout`'s own doc comment. Omitted (the single-branch
  // Home's own default) means the dedicated `dashboard_config` field.
  preferenceKey?: string
  // Company HQ's own page-level branch filter — a fixed list of team ids
  // to narrow every tile by, or `undefined`/empty for "all accessible
  // branches". Never used outside Company HQ.
  teamScope?: string[]
}

export const WidgetsDashboard = ({
  mainColumnFooter,
  preferenceKey,
  renderHeader,
  sideColumnFooter,
  teamScope
}: WidgetsDashboardProps = {}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const { window, globalFilters, nba } = useAppSelector((s) => s.portfolio)
  const engagementHost = useEngagementWidgetHost()
  // Bumped by the table card's Refresh: a new loader re-reads every span.
  const [engagementGeneration, setEngagementGeneration] = useState(0)
  // Keyed on the filters' values: restoring the same filters must not re-read
  // the table's spans.
  const engagementFilterParams = JSON.stringify(
    buildFilterParams(globalFilters ?? undefined)
  )
  const engagementHandlers = useMemo(
    () => {
      const params = JSON.parse(engagementFilterParams) as Record<
        string,
        string
      >
      return engagementHost.bind({
        resolve: (span) =>
          resolveDashboardWidget(
            ENGAGEMENT_INSTANCE,
            span,
            globalFilters ?? undefined
          ),
        drillFilters: prefixFilterParams("dash_", params)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engagementHost.bind, engagementFilterParams, engagementGeneration]
  )
  const {
    status,
    fellBack,
    widgets,
    resolvedByKey,
    pendingResolveIds,
    generatedAt,
    catalog,
    catalogStatus
  } = useAppSelector((s) => s.widgets)
  const financePermissions = useAppSelector(
    (s) => s.auth?.permissions?.codes?.projects?.finances
  )

  const windowRef = useRef(window)
  windowRef.current = window
  const didInit = useRef(false)
  const inited = useRef(false)
  const pendingConfig = useRef<{ has: boolean; raw: unknown }>({
    has: false,
    raw: null
  })
  const [prefsTick, setPrefsTick] = useState(0)
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null)
  const [selectedWindowPreset, setSelectedWindowPreset] = useState(
    window.preset
  )
  const [addTileOpen, setAddTileOpen] = useState(false)
  const [pendingViewId, setPendingViewId] = useState<string | null>(null)
  // `useBlocker` is only legal under a data router; the app has one, the
  // specs and the Portfolio fallback do not.
  const inDataRouter = useContext(UNSAFE_DataRouterContext) != null

  useEffect(() => {
    setSelectedWindowPreset(window.preset)
  }, [window.preset])

  const specIndex = useMemo(() => indexCatalog(catalog), [catalog])

  // Absent permissions mean "not loaded yet" — keep the slot rather than
  // flicker it away for everyone.
  const fixedInstances = useMemo(
    () =>
      fixedWidgetInstancesFor(
        !financePermissions ||
          Boolean(
            financePermissions.project_finance_view ||
            financePermissions.project_finance_full_access
          )
      ),
    [financePermissions]
  )

  const layout = useDashboardLayout({
    version: 4,
    resolveInstance: (tileId: string) => liftTileToInstance(tileId, specIndex),
    fixedInstances,
    preferenceKey
  })

  // Company HQ's page-level branch filter, mirrored into `state.portfolio`
  // before anything fetches — declared ahead of the init effect below so
  // the very first `fetchWidgets` call already reads the right scope
  // rather than firing once unscoped and once correctly. A later change
  // (the user picks a different set of branches) explicitly refetches;
  // the very first run does not, since the init effect's own fetch covers
  // it. Never written back into a saved view — see `setTeamScope`.
  const teamScopeKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const key = JSON.stringify([teamScope ?? [], preferenceKey])
    if (teamScopeKeyRef.current === key) return
    const isFirst = teamScopeKeyRef.current === undefined
    teamScopeKeyRef.current = key
    dispatch(setTeamScope(teamScope ?? []))
    dispatch(setConfigKey(preferenceKey))
    if (!isFirst) void dispatch(fetchWidgets(windowRef.current))
  }, [teamScope, preferenceKey, dispatch])

  const { options: filterOptions, loading: filterOptionsLoading } =
    useGlobalFilterOptions()

  // The keyboard sensor is what makes the metric grid reorderable without a
  // pointer: Space lifts the focused handle, the arrows move it, Space drops.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  useEffect(() => {
    if (!layout.isEditing) setAddTileOpen(false)
  }, [layout.isEditing])

  // The Product Tour's dashboard-customization steps drive real Edit Layout
  // state through these two query params — the same `?addMember=1`-style
  // convention the Setup Assistant's Team step already uses to open a real
  // UI state via the URL rather than the tour engine reaching into this
  // component directly. Nothing here ever persists: entering edit mode this
  // way is exactly `startEdit()`, the same pure-local-state call the real
  // "Edit Layout" button makes (omitting its explicit order seed falls back
  // to the layout's own current order internally), and the tour never calls
  // `save()`. Declared here, before any of this component's early returns
  // (loading/error/fellBack below), so these hooks always run in the same
  // order every render — moving them lower once broke exactly that.
  const [tourSearchParams, setTourSearchParams] = useSearchParams()
  const tourEdit = tourSearchParams.get("tourEdit") === "1"
  const tourAddTile = tourSearchParams.get("tourAddTile") === "1"
  const { isActive: productTourActive } = useProductTour()
  useEffect(() => {
    if (tourEdit && !layout.isEditing) layout.startEdit()
    else if (!tourEdit && layout.isEditing) layout.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourEdit])
  useEffect(() => {
    if (tourAddTile) setAddTileOpen(true)
  }, [tourAddTile])
  // `ProductTourContext.stop()` (Escape, or the overlay's Close/Skip) only
  // resets the tour's own state — it never touches the router. Left alone,
  // exiting early on a dashboard-editing step leaves `tourEdit`/`tourAddTile`
  // in the URL: the real dashboard stays silently in Edit Layout mode, and a
  // reload, Back, or a bookmark of this exact link re-triggers it again later
  // with zero tour activity. Stripping the params here lets the two effects
  // above run their normal cleanup (`layout.cancel()`, which in turn closes
  // the Add Tile drawer via the `!layout.isEditing` effect) exactly as if the
  // tour had moved on to its next step and navigated away cleanly.
  useEffect(() => {
    if (productTourActive || !(tourEdit || tourAddTile)) return
    setTourSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("tourEdit")
        next.delete("tourAddTile")
        return next
      },
      { replace: true }
    )
  }, [productTourActive, tourEdit, tourAddTile, setTourSearchParams])

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true
      void dispatch(fetchCatalog())
      void dispatch(fetchWidgets(windowRef.current))
      void dispatch(fetchNba())
      void (async () => {
        try {
          const prefs = await getUIPreferences()
          const raw =
            prefs.ok && prefs.data
              ? preferenceKey && preferenceKey !== "dashboard_config"
                ? ((
                    prefs.data.preferences as
                      | Record<string, unknown>
                      | undefined
                  )?.[preferenceKey] ?? null)
                : prefs.data.dashboard_config
              : null
          pendingConfig.current = { has: true, raw }
        } catch {
          pendingConfig.current = { has: true, raw: null }
        }
        setPrefsTick((n) => n + 1)
      })()
    }

    if (fellBack) return

    const intervalId = setInterval(() => {
      void dispatch(fetchNba())
    }, NBA_POLL_INTERVAL)

    return () => clearInterval(intervalId)
  }, [dispatch, fellBack])

  // KPI titles come from the catalog; a failed fetch would otherwise leave raw
  // metric keys on screen for the whole session (the landing page mounts inside
  // the login/cold-boot request burst, where one-off failures are likely).
  const catalogRetries = useRef(0)
  useEffect(() => {
    if (fellBack || catalogStatus !== "error") return
    if (catalogRetries.current >= CATALOG_MAX_RETRIES) return
    const attempt = catalogRetries.current + 1
    const timer = setTimeout(() => {
      catalogRetries.current = attempt
      void dispatch(fetchCatalog())
    }, CATALOG_RETRY_BASE_MS * attempt)
    return () => clearTimeout(timer)
  }, [catalogStatus, fellBack, dispatch])

  // The v3→v4 lift resolves tile ids through the catalog, so hold init until the
  // catalog and the stored preferences are both in hand.
  useEffect(() => {
    if (inited.current || !catalog || !pendingConfig.current.has) return
    inited.current = true
    const raw = pendingConfig.current.raw
    layout.initFromConfig(raw)
    const restored = parseActiveViewGlobalFilters(raw)
    dispatch(setGlobalFilters(restored))
    if (hasActiveFilters(restored)) {
      void dispatch(fetchWidgets(windowRef.current))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, prefsTick])

  useEffect(() => {
    if (widgets.length) {
      layout.registerInstances(responseToInstances(widgets, specIndex))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets, specIndex])

  const windowKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (fellBack) return
    const key = JSON.stringify(window)
    if (windowKeyRef.current === undefined) {
      windowKeyRef.current = key
      return
    }
    if (windowKeyRef.current !== key) {
      windowKeyRef.current = key
      void dispatch(fetchWidgets(window))
    }
  }, [window, dispatch, fellBack])

  const applyFiltersDebounced = useDebouncedCallback(
    (next: GlobalFilters, nextLabels: FilterLabels, viewId: string | null) => {
      void dispatch(fetchWidgets(windowRef.current))
      void layout.persistGlobalFilters(next, nextLabels, viewId).catch(() => {
        void message.error(
          t("portfolio.filters.persistFailed", "Failed to save filters")
        )
      })
    },
    350
  )

  const currency = useMemo(() => currencyFromWidgets(widgets), [widgets])
  const hasFreshSnapshot =
    widgets.length > 0 && isWidgetSnapshotFresh(generatedAt)
  const generatedAtLabel = useMemo(() => {
    if (!generatedAt) return ""
    const parsed = new Date(generatedAt)
    if (Number.isNaN(parsed.getTime())) return ""
    return new Intl.DateTimeFormat(
      i18n.language?.startsWith("de") ? "de-DE" : "en-US",
      {
        dateStyle: "medium",
        timeStyle: "short"
      }
    ).format(parsed)
  }, [generatedAt, i18n.language])

  const adaptedByKey = useMemo(() => {
    const out: Record<string, AdaptedWidget> = {}
    for (const [key, raw] of Object.entries(resolvedByKey)) {
      out[key] = adaptResolved(raw, specIndex, currency)
    }
    return out
  }, [resolvedByKey, specIndex, currency])

  const kpiAdapted = (slug: string): AdaptedWidget | undefined =>
    adaptedByKey[valueKey("kpi", metricFromSlug(slug))]

  const responseKpiSlugs = useMemo(() => {
    const slugs: string[] = []
    for (const w of widgets) {
      if (w.type !== "kpi") continue
      const s = resolvedSlug(w, specIndex)
      if (s) slugs.push(s)
    }
    return slugs
  }, [widgets, specIndex])

  const responseBoardSlugs = useMemo(() => {
    const slugs: string[] = []
    for (const w of widgets) {
      const s = resolvedSlug(w, specIndex)
      if (s && isBoardSlug(s)) slugs.push(s)
    }
    return slugs
  }, [widgets, specIndex])

  const responseSnapshotSlugs = useMemo(() => {
    const slugs: string[] = []
    for (const w of widgets) {
      if (w.type !== "snapshot") continue
      const s = resolvedSlug(w, specIndex)
      if (s) slugs.push(s)
    }
    return slugs
  }, [widgets, specIndex])

  const responseSlugSet = useMemo(
    () =>
      new Set([
        ...responseKpiSlugs,
        ...responseBoardSlugs,
        ...responseSnapshotSlugs
      ]),
    [responseKpiSlugs, responseBoardSlugs, responseSnapshotSlugs]
  )

  if (fellBack) {
    return (
      <>
        {renderHeader?.(null, null)}
        <PortfolioDashboard />
      </>
    )
  }

  if (status === "loading" || status === "idle") {
    return (
      <Flex vertical gap="middle">
        {renderHeader?.(null, null)}
        <Row gutter={[16, 16]}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Col key={i} {...KPI_CARD_COL_WIDE}>
              <WidgetRenderer
                widget={{ id: `sk${i}`, type: "kpi" } as WidgetInstance}
                payload={null}
                state="loading"
              />
            </Col>
          ))}
        </Row>
      </Flex>
    )
  }

  if (status === "error" && !hasFreshSnapshot) {
    return (
      <>
        {renderHeader?.(null, null)}
        <Result
          status="error"
          title={t("portfolio.error.title")}
          extra={
            <Button onClick={() => void dispatch(fetchWidgets(window))}>
              {t("portfolio.error.retry")}
            </Button>
          }
        />
      </>
    )
  }

  const kpiOrder = (
    layout.order.length ? layout.order : responseKpiSlugs
  ).filter(isKpiSlug)
  const shownKpiSlugs = kpiOrder.filter((slug) => !layout.hidden.includes(slug))
  // A layout the user has customised keeps exactly the order it stored.
  // Only a layout with none falls back to the reference's reading order —
  // money first, then the project counts. `layout.order` alone is the wrong
  // signal: it is seeded from the widgets response for everyone.
  const orderedKpiSlugs = layout.hasStoredOrder
    ? shownKpiSlugs
    : [...shownKpiSlugs].sort((a, b) => {
        const rank = (slug: string) => {
          const index = DEFAULT_METRIC_ORDER.indexOf(
            metricFromSlug(slug) as (typeof DEFAULT_METRIC_ORDER)[number]
          )
          return index === -1 ? DEFAULT_METRIC_ORDER.length : index
        }
        return rank(a) - rank(b)
      })
  const shownSet = new Set(shownKpiSlugs)

  const boardBase = layout.order.length ? layout.order : responseBoardSlugs
  const boardOrder = boardBase
    .filter(isBoardSlug)
    .filter((slug) => !layout.hidden.includes(slug))
  // A chart placed on the board sits under Portfolio Overview in that card's
  // own grid column, so the two always share one width whatever the layout.
  const boardChartOrder = boardOrder.filter(
    (slug) => typeFromSlug(slug) === "chart"
  )
  // The Location tile reuses that exact same column width — explicit product
  // direction, so it reads as one deliberate section rather than a
  // full-bleed table/list row. `BoardWidgetCard` renders a plain full-width
  // block, so nesting it one level inside `dashboardRowGrid`'s left (68fr)
  // cell — rather than adding a width override prop — is enough.
  const mapOrder = boardOrder.filter((slug) => typeFromSlug(slug) === "map")
  const boardListOrder = boardOrder.filter(
    (slug) => typeFromSlug(slug) !== "chart" && typeFromSlug(slug) !== "map"
  )
  const boardAdapted = (slug: string): AdaptedWidget | undefined =>
    adaptedByKey[valueKey(typeFromSlug(slug), metricFromSlug(slug))]

  const feedAlertOrder = layout.order
    .filter(isFeedAlertSlug)
    .filter((slug) => !layout.hidden.includes(slug))

  const snapshotBase = layout.order.length
    ? layout.order
    : responseSnapshotSlugs
  const snapshotOrder = snapshotBase
    .filter(isSnapshotSlug)
    .filter((slug) => !layout.hidden.includes(slug))

  // Seeding the order from one section would drop every other section's widgets
  // from the layout the moment Edit Layout is opened, and saving would persist
  // the loss — the snapshot card vanished until Restore Default.
  // The metric grid's own displayed order, not the raw response order: editing
  // begins from what is on screen, so the first drag moves one tile instead of
  // re-sorting the whole grid as the default arrangement falls away.
  const editableOrder = [
    ...orderedKpiSlugs,
    ...kpiOrder.filter((slug) => !orderedKpiSlugs.includes(slug)),
    ...snapshotOrder,
    ...boardOrder,
    ...feedAlertOrder
  ]

  // Add Tile offers whatever is not on screen: a removed tile comes back from
  // there, not only one that was never added.
  const boardShown = new Set(boardOrder)
  const feedAlertShown = new Set(feedAlertOrder)
  const snapshotShown = new Set(snapshotOrder)
  const cashFlowShown = !layout.hidden.includes(CASH_FLOW_SLUG)
  const projectStatusShown = !layout.hidden.includes(PROJECT_STATUS_SLUG)
  const nbaShown = !layout.hidden.includes(NBA_FEED_SLUG)
  // The rail is a declared grid track only when it has content. Next Best
  // Actions renders nothing when the feed is empty, so on a quiet account the
  // rail is the notifications panel alone and it starts at the top of the
  // body instead of under a reserved gap.
  const nbaRendered = nbaShown && (nba.length > 0 || layout.isEditing)
  const hasSideRail = nbaRendered || Boolean(sideColumnFooter)
  const builtInEntries = [
    ...(fixedInstances.some((instance) => instance.slug === CASH_FLOW_SLUG)
      ? [{ id: CASH_FLOW_SLUG, title: t("portfolio.cashFlow.title") }]
      : []),
    { id: PROJECT_STATUS_SLUG, title: t("portfolio.status.title") },
    { id: NBA_FEED_SLUG, title: t("portfolio.nba.title", "Next Best Actions") }
  ]

  // Every widget the caller may add, each carrying whether the DRAFT already
  // holds it. The catalog is never pruned to "what is left": a widget already
  // on the dashboard still shows, marked, so a search for it does not come
  // back empty and leave the user wondering whether it exists at all. The
  // permission filter stays the backend's — `catalog.widgets` excludes
  // `restricted_widgets` by construction.
  const entryFor = (
    spec: CatalogWidgetSpec,
    shown: Set<string>
  ): CatalogEntry => {
    const id = slugFor(spec.data_source, spec.metric, spec.type)
    return { id, title: spec.title, spec, alreadyAdded: shown.has(id) }
  }
  const catalogEntries: CatalogEntry[] = [
    ...catalogKpiSpecs(catalog).map((spec) => entryFor(spec, shownSet)),
    ...catalogBoardSpecs(catalog).map((spec) => entryFor(spec, boardShown)),
    ...catalogFeedAlertSpecs(catalog).map((spec) =>
      entryFor(spec, feedAlertShown)
    ),
    ...catalogSnapshotSpecs(catalog).map((spec) =>
      entryFor(spec, snapshotShown)
    ),
    // The three fixed slots are not in the catalog; they are offered here so a
    // removed Cash Flow, Project Status or Next Best Actions can come back.
    ...builtInEntries.map((entry) => ({
      id: entry.id,
      title: entry.title,
      spec: {
        key: entry.id,
        type: typeFromSlug(entry.id),
        title: entry.title,
        data_source: entry.id.split(".")[0] ?? "",
        metric: metricFromSlug(entry.id),
        default_config: {},
        capabilities: [],
        permission: null,
        stub: false,
        drill_to: ""
      } satisfies CatalogWidgetSpec,
      alreadyAdded: !layout.hidden.includes(entry.id)
    }))
  ]

  const cashFlowAdapted = adaptedByKey[valueKey("chart", "cash_flow_series")]
  const donutAdapted =
    adaptedByKey[valueKey("chart", "project_status_breakdown")]

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      layout.reorder(String(active.id), String(over.id))
    }
  }

  // Answers whether the layout actually persisted. A refused save leaves the
  // draft and edit mode exactly as they were, so the retry is the same button.
  const handleSave = async (): Promise<boolean> => {
    try {
      await layout.save()
      void dispatch(fetchWidgets(windowRef.current))
      return true
    } catch {
      void message.error(t("portfolio.editLayout.saveFailed"))
      return false
    }
  }

  const handleFiltersChange = (
    next: GlobalFilters,
    nextLabels: FilterLabels
  ) => {
    dispatch(setGlobalFilters(next))
    applyFiltersDebounced(next, nextLabels, layout.activeViewId)
  }

  const switchNow = async (id: string) => {
    try {
      const target = layout.views.find((v) => v.id === id)
      await layout.switchView(id)
      dispatch(setGlobalFilters(parseGlobalFilters(target)))
      void dispatch(fetchWidgets(windowRef.current))
    } catch {
      void message.error(
        t("portfolio.editLayout.switchFailed", "Failed to switch view")
      )
    }
  }

  // Switching views discards the draft as surely as leaving the page does, so
  // it asks the same question first.
  const handleSwitch = async (id: string) => {
    if (!layout.isEditing || !layout.isDirty) {
      await switchNow(id)
      return
    }
    setPendingViewId(id)
  }

  const discardDraftForSwitch = () => {
    const target = pendingViewId
    setPendingViewId(null)
    layout.cancel()
    if (target) void switchNow(target)
  }

  const saveDraftForSwitch = () => {
    const target = pendingViewId
    void handleSave().then((ok) => {
      if (!ok) return
      setPendingViewId(null)
      if (target) void switchNow(target)
    })
  }

  const handleCreate = async (name: string, from: "current" | "default") => {
    try {
      const source =
        from === "default"
          ? layout.views.find((view) => view.id === layout.defaultViewId)
          : layout.views.find((view) => view.id === layout.activeViewId)
      await layout.createView(name, from)
      dispatch(setGlobalFilters(parseGlobalFilters(source)))
      void dispatch(fetchWidgets(windowRef.current))
    } catch {
      void message.error(
        t("portfolio.editLayout.createFailed", "Failed to create view")
      )
    }
  }

  // Leaving Home — or switching to another view — with a dirty draft offers the
  // same three answers rather than silently keeping or silently dropping it.
  const handleRename = async (id: string, name: string) => {
    try {
      await layout.renameView(id, name)
    } catch {
      void message.error(
        t("portfolio.editLayout.renameFailed", "Failed to rename view")
      )
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const fallback =
        id === layout.activeViewId
          ? layout.views.find((view) => view.id !== id)
          : undefined
      await layout.deleteView(id)
      if (fallback) {
        dispatch(setGlobalFilters(parseGlobalFilters(fallback)))
        void dispatch(fetchWidgets(windowRef.current))
      }
    } catch {
      void message.error(
        t("portfolio.editLayout.deleteFailed", "Failed to delete view")
      )
    }
  }

  const handleSetDefault = async (id: string) => {
    try {
      await layout.setDefault(id)
    } catch {
      void message.error(
        t("portfolio.editLayout.setDefaultFailed", "Failed to set default")
      )
    }
  }

  // Resets the DRAFT only: the saved layout is untouched until Save layout.
  const handleResetDraft = () => {
    layout.resetDraftToDefault()
    void message.info(
      t(
        "portfolio.editLayout.resetDraftDone",
        "Draft reset to the default layout. Save layout to keep it."
      )
    )
  }

  const handleRestoreDefault = async () => {
    try {
      await layout.restoreDefault()
      dispatch(setGlobalFilters(createEmptyFilters()))
      void dispatch(fetchWidgets(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.restoreFailed"))
    }
  }

  // The picker commits a batch. Each goes through the same single-slug path, so
  // a built-in simply un-hides and a catalog widget is resolved exactly as a
  // one-at-a-time add always did.
  const handleAddMany = (slugs: string[]) => {
    for (const slug of slugs) handleAdd(slug)
    setAddTileOpen(false)
  }

  const handleAdd = (slug: string) => {
    if (BUILT_IN_SLUGS.includes(slug)) {
      if (layout.hidden.includes(slug)) layout.toggleHidden(slug)
      return
    }
    const spec = specIndex.bySlug.get(slug)
    if (!spec) return
    const instance = specToInstance(spec)
    layout.addWidget(instance)
    void dispatch(resolveWidget({ instance, window: windowRef.current }))
  }

  const handleHide = (slug: string) => {
    layout.toggleHidden(slug)
    if (!BUILT_IN_SLUGS.includes(slug) && !responseSlugSet.has(slug)) {
      dispatch(
        dropResolvedWidget(valueKey(typeFromSlug(slug), metricFromSlug(slug)))
      )
    }
  }

  const refetch = () => void dispatch(fetchWidgets(windowRef.current))

  const resolveKeyOf = (slug: string) =>
    valueKey(typeFromSlug(slug), metricFromSlug(slug))

  const isRefreshing = (slug: string) =>
    Boolean(pendingResolveIds[resolveKeyOf(slug)])

  const handleWidgetRefresh = (slug: string) => {
    if (metricFromSlug(slug) === ENGAGEMENT_METRIC)
      setEngagementGeneration((value) => value + 1)
    const fixed = fixedWidgetInstances().find((i) => i.slug === slug)
    const spec = specIndex.bySlug.get(slug)
    const instance = fixed ?? (spec ? specToInstance(spec) : null)
    if (!instance) {
      refetch()
      return
    }
    void dispatch(resolveWidget({ instance, window: windowRef.current }))
  }

  const expandedAdapted = expandedSlug
    ? adaptedByKey[resolveKeyOf(expandedSlug)]
    : undefined

  const cardStyle: React.CSSProperties = homeCardStyle

  // Financial Snapshot and Portfolio overview keep their own natural height —
  // that height is what defines each grid row. Cash Flow and Project Status
  // sit in the shorter/follower column and need to visually reach the same
  // top+bottom edges, which is the one place a card's outer box legitimately
  // needs `height: 100%` (the grid stretches the *cell*, but nothing makes
  // the *card inside it* look that tall unless the card says so too).
  //
  // The card must also be `display: flex; flex-direction: column` for that
  // to do anything — without it, `height: 100%` sizes the card correctly but
  // its body still sizes to its own content, so nothing downstream that
  // depends on a definite pixel height (recharts' `ResponsiveContainer
  // height="100%"`, in particular) has one to resolve against and silently
  // renders at zero height. That gap — Card said 100% tall, body never
  // participated in flex, so recharts collapsed to nothing — is what made
  // the Cash Flow chart disappear on the first pass of this fix.
  const stretchCardStyle: React.CSSProperties = {
    ...cardStyle,
    // Its wrapper is a flex column that already owns the cell's height, so the
    // card grows into what is left after an edit-mode Remove bar rather than
    // claiming 100% of the cell and overflowing it.
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
  }
  const stretchCardBodyBase: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
  }
  // Cash Flow: the chart should actually grow into the extra height (a
  // taller time-series chart is more useful, not "empty") — content stretches.
  const stretchBodyStyle: React.CSSProperties = {
    ...stretchCardBodyBase,
    padding: "16px 20px"
  }
  const centerBodyStyle: React.CSSProperties = {
    ...stretchBodyStyle,
    justifyContent: "center"
  }
  const fillContentStyle: React.CSSProperties = { flex: 1, minHeight: 128 }
  // Project Status: stretching the donut ring itself to match a much taller
  // Portfolio overview would blow it up into an oversized ring, which reads
  // as worse, not better. Keep the donut+legend at their natural size and
  // centre that block in the extra height instead, so leftover space becomes
  // even padding rather than a dead gap under the legend.

  // Both major rows share this exact template so the two columns line up
  // between rows (Financial Snapshot's left edge = Portfolio overview's left
  // edge, Cash Flow's right edge = Project Status's right edge) — the grid,
  // not the individual cards, owns the widths.
  const dashboardRowColumns =
    "grid grid-cols-1 xl:grid-cols-[minmax(0,68fr)_minmax(300px,32fr)] gap-4"
  const dashboardRowGrid = `${dashboardRowColumns} items-stretch`
  // Two Tailwind utilities for one property resolve by stylesheet order, not
  // class order, so the financial row composes its own alignment rather than
  // appending `items-start` after `items-stretch`.
  const dashboardRowGridTop = `${dashboardRowColumns} items-start`
  // Portfolio overview now runs the full width, and a chart placed on the
  // board still has to share that width — so both sit in this one-cell grid.
  const fullWidthRowGrid = "grid grid-cols-1"

  const toolbar = (
    viewControls: React.ReactNode,
    exportControl: React.ReactNode
  ) =>
    renderHeader ? (
      renderHeader(viewControls, exportControl)
    ) : (
      <Flex justify="space-between" align="center" wrap="wrap" gap="small">
        {viewControls}
        {exportControl}
      </Flex>
    )

  return (
    <Flex
      vertical
      gap="middle"
      data-guide-target="home-dashboard-panel"
      className={layout.isEditing ? "home-editing" : undefined}
      style={{ ["--home-edit-outline" as string]: homeColor.editOutline }}
    >
      {status === "error" && hasFreshSnapshot && (
        <Alert
          showIcon
          type="warning"
          message={t("portfolio.error.savedSnapshot", {
            time: generatedAtLabel
          })}
          action={
            <Button
              size="small"
              onClick={() => void dispatch(fetchWidgets(window))}
            >
              {t("portfolio.error.retry")}
            </Button>
          }
        />
      )}
      {toolbar(
        <SavedViewBar
          views={layout.views}
          activeViewId={layout.activeViewId}
          activeView={layout.activeView}
          defaultViewId={layout.defaultViewId}
          isEditing={layout.isEditing}
          startEdit={() => layout.startEdit(editableOrder)}
          cancel={layout.cancel}
          save={async () => void (await handleSave())}
          restoreDefault={handleRestoreDefault}
          onAddTile={() => setAddTileOpen(true)}
          onSwitch={handleSwitch}
          onCreate={handleCreate}
          onRename={handleRename}
          onDelete={handleDelete}
          onSetDefault={handleSetDefault}
          // This component always draws `EditLayoutBar`, whoever hosts it, so
          // the view row never repeats Save / Cancel / Reset / Add widget.
          // Only the legacy `PortfolioDashboard`, which has no such bar,
          // leaves them on.
          showEditingControls={false}
        />,
        <ExportButtons
          window={window}
          filters={globalFilters ?? undefined}
          selectedPreset={selectedWindowPreset}
          teamScope={teamScope}
        />
      )}

      {layout.isEditing && (
        <EditLayoutBar
          viewName={layout.activeView?.name ?? null}
          saving={layout.saving}
          dirty={layout.isDirty}
          onAddWidget={() => setAddTileOpen(true)}
          onResetToDefault={handleResetDraft}
          onCancel={layout.cancel}
          onSave={() => void handleSave()}
        />
      )}

      <FilterBar
        filters={globalFilters ?? parseGlobalFilters(null)}
        options={filterOptions}
        filterLabels={layout.filterLabels}
        dateControl={
          <div data-testid="dashboard-date-filter">
            <TimeWindowControl
              selectedPreset={selectedWindowPreset}
              onSelectedPresetChange={setSelectedWindowPreset}
            />
          </div>
        }
        loading={filterOptionsLoading}
        onChange={handleFiltersChange}
      />

      {/* The two-column body: a flexible main column and a compact right
          rail. Both children carry `min-w-0` — a grid child's default
          `min-width: auto` is what lets a wide table push the page sideways,
          and that is the overflow's source rather than something to clip. */}
      {/* Portfolio Overview runs the full width above the columns. */}
      {/* Portfolio overview leads the page: the eight metrics are what a Home
          visit is for, so they run the full width above the recommendations
          and the financial detail rather than sharing a row with the donut
          (2026-10-06 Home redesign). */}
      <div className={fullWidthRowGrid}>
        <div
          style={{ minWidth: 0 }}
          data-guide-target="dashboard-rearrange-panel"
          data-dashboard-row-cell
        >
          {/* One surface, eight cells divided by hairlines — not eight
              outlined cards inside an outlined card, which is what made the
              previous pass read as a stack of identical boxes. */}
          <section style={{ ...homeCardStyle, overflow: "hidden" }}>
            <HomeSectionHeader
              title={t("portfolio.section.overview", "Portfolio Overview")}
              caption={t(
                "portfolio.section.selectedPeriod",
                "Selected period: {{period}}",
                {
                  period: t(`portfolio.window.${window.preset}`)
                }
              )}
            />
            <div className="home-metric-grid-wrap">
              <div
                className="home-metric-grid"
                style={{ ["--home-metric-border" as string]: homeColor.border }}
              >
                {layout.isEditing ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={orderedKpiSlugs}
                      strategy={rectSortingStrategy}
                    >
                      {orderedKpiSlugs.map((slug) => (
                        <SortableKpiCard
                          key={slug}
                          slug={slug}
                          adapted={kpiAdapted(slug)}
                          currency={currency}
                          onHide={handleHide}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  orderedKpiSlugs.map((slug) => (
                    <StaticKpiCard
                      key={slug}
                      slug={slug}
                      adapted={kpiAdapted(slug)}
                      currency={currency}
                    />
                  ))
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* The body is two columns: a flexible main column carrying the
          financial detail, and a compact right rail. Both carry `min-w-0` —
          a grid child's default `min-width: auto` is its content's width,
          which is what lets a wide table push the page sideways, and that is
          the overflow's source rather than something to clip.

          The rail's track is declared only when there is something to put in
          it (`data-home-rail`), so a Home with no Next Best Actions and no
          notifications gives the whole width back instead of leaving a
          reserved empty column. */}
      <div className="home-body-grid-wrap">
        <div className="home-body-grid" data-home-rail={String(hasSideRail)}>
          <div className="flex flex-col gap-4 min-w-0" data-home-col="main">
            {snapshotOrder.length > 0 && (
              <div
                style={{ minWidth: 0 }}
                className="home-panel"
                data-guide-target="home-financial-snapshot-panel"
              >
                <div className="flex flex-col gap-4 min-w-0">
                  {snapshotOrder.map((slug) => (
                    <BoardWidgetCard
                      key={slug}
                      slug={slug}
                      adapted={boardAdapted(slug)}
                      currency={currency}
                      isEditing={layout.isEditing}
                      refreshing={isRefreshing(slug)}
                      onHide={handleHide}
                      onRefresh={() => handleWidgetRefresh(slug)}
                    />
                  ))}
                </div>
              </div>
            )}
            {mapOrder.length > 0 && (
              <div className={dashboardRowGrid}>
                <div style={{ minWidth: 0 }} data-dashboard-row-cell>
                  <div className="flex flex-col gap-4 min-w-0">
                    {mapOrder.map((slug) => (
                      <BoardWidgetCard
                        key={slug}
                        slug={slug}
                        adapted={boardAdapted(slug)}
                        currency={currency}
                        isEditing={layout.isEditing}
                        refreshing={isRefreshing(slug)}
                        onHide={handleHide}
                        onRefresh={() => handleWidgetRefresh(slug)}
                        onExpand={() => setExpandedSlug(slug)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {boardChartRows(boardChartOrder).map((row) => {
              const cards = row.slugs.map((slug) => (
                <BoardWidgetCard
                  key={slug}
                  slug={slug}
                  adapted={boardAdapted(slug)}
                  currency={currency}
                  isEditing={layout.isEditing}
                  refreshing={isRefreshing(slug)}
                  onHide={handleHide}
                  onRefresh={() => handleWidgetRefresh(slug)}
                  onExpand={() => setExpandedSlug(slug)}
                  onEngagementDrill={engagementHandlers.onEngagementDrill}
                  loadEngagementRange={engagementHandlers.loadEngagementRange}
                />
              ))
              return row.wide ? (
                <div
                  key={row.slugs[0]}
                  className="grid grid-cols-1"
                  style={{ minWidth: 0 }}
                >
                  {cards}
                </div>
              ) : (
                <div key={row.slugs[0]} className={fullWidthRowGrid}>
                  <div
                    style={{ minWidth: 0 }}
                    className="flex flex-col gap-4"
                    data-dashboard-row-cell
                  >
                    {cards}
                  </div>
                </div>
              )
            })}

            {(cashFlowShown || projectStatusShown) && (
              <div className="home-chart-pair-wrap">
                <div className="home-chart-pair">
                  {cashFlowShown && (
                    <div style={{ minWidth: 0 }} className="flex flex-col">
                      <section
                        style={stretchCardStyle}
                        data-widget-card={CASH_FLOW_SLUG}
                      >
                        <HomeSectionHeader
                          title={t("portfolio.cashFlow.title")}
                          icon={ChartNoAxesCombinedIcon}
                          editChrome={
                            layout.isEditing ? (
                              <WidgetRemoveButton
                                title={t("portfolio.cashFlow.title")}
                                onRemove={() => handleHide(CASH_FLOW_SLUG)}
                              />
                            ) : null
                          }
                          extra={
                            <CapabilityActions
                              capabilities={
                                cashFlowAdapted?.widget.capabilities
                              }
                              onRefresh={() =>
                                handleWidgetRefresh(CASH_FLOW_SLUG)
                              }
                              onExpand={() => setExpandedSlug(CASH_FLOW_SLUG)}
                              onExport={() =>
                                exportWidgetCsv({
                                  title: t("portfolio.cashFlow.title"),
                                  payload: cashFlowAdapted?.payload
                                })
                              }
                            />
                          }
                        />
                        <div style={stretchBodyStyle}>
                          {isRefreshing(CASH_FLOW_SLUG) ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                          ) : (
                            <div style={fillContentStyle}>
                              <WidgetRenderer
                                widget={
                                  cashFlowAdapted?.widget ??
                                  ({
                                    id: CASH_FLOW_SLUG,
                                    type: "chart"
                                  } as WidgetInstance)
                                }
                                payload={
                                  cashFlowAdapted?.payload ?? {
                                    chart: {
                                      visualisation: "composed",
                                      cashFlow: { series: [] }
                                    }
                                  }
                                }
                                state="ready"
                                fillHeight
                              />
                            </div>
                          )}
                        </div>
                      </section>
                    </div>
                  )}
                  {projectStatusShown && (
                    <div style={{ minWidth: 0 }} className="flex flex-col">
                      <section
                        style={stretchCardStyle}
                        data-widget-card={PROJECT_STATUS_SLUG}
                      >
                        <HomeSectionHeader
                          title={t("portfolio.status.title")}
                          icon={ChartPieIcon}
                          editChrome={
                            layout.isEditing ? (
                              <WidgetRemoveButton
                                title={t("portfolio.status.title")}
                                onRemove={() => handleHide(PROJECT_STATUS_SLUG)}
                              />
                            ) : null
                          }
                          extra={
                            <CapabilityActions
                              capabilities={donutAdapted?.widget.capabilities}
                              onRefresh={() =>
                                handleWidgetRefresh(PROJECT_STATUS_SLUG)
                              }
                              onExpand={() =>
                                setExpandedSlug(PROJECT_STATUS_SLUG)
                              }
                              onExport={() =>
                                exportWidgetCsv({
                                  title: t("portfolio.status.title"),
                                  payload: donutAdapted?.payload
                                })
                              }
                            />
                          }
                        />
                        <div style={centerBodyStyle}>
                          {isRefreshing(PROJECT_STATUS_SLUG) ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                          ) : (
                            // No `fillHeight` here on purpose: stretching the donut ring
                            // itself to match a much taller neighbour would blow it up
                            // into an oversized ring. It stays its normal size and the
                            // flex body (`centerCardStyles`) centres it instead.
                            <WidgetRenderer
                              widget={
                                donutAdapted?.widget ??
                                ({
                                  id: PROJECT_STATUS_SLUG,
                                  type: "chart"
                                } as WidgetInstance)
                              }
                              payload={
                                donutAdapted?.payload ?? {
                                  chart: {
                                    visualisation: "donut",
                                    status: {
                                      on_track: 0,
                                      at_risk: 0,
                                      delayed: 0,
                                      total: 0
                                    }
                                  }
                                }
                              }
                              state="ready"
                            />
                          )}
                        </div>
                      </section>
                    </div>
                  )}
                </div>
              </div>
            )}
            {mainColumnFooter}
          </div>

          {hasSideRail && (
            <div
              className="flex flex-col gap-4 min-w-0"
              data-home-col="actions"
            >
              {nbaRendered && (
                <div>
                  {nba.length > 0 ? (
                    <WidgetRenderer
                      {...feedWidget(nba)}
                      state="ready"
                      editChrome={
                        layout.isEditing ? (
                          <WidgetRemoveButton
                            title={t(
                              "portfolio.nba.title",
                              "Next Best Actions"
                            )}
                            onRemove={() => handleHide(NBA_FEED_SLUG)}
                          />
                        ) : null
                      }
                    />
                  ) : (
                    // An empty feed renders nothing; while editing it still needs
                    // a card, or there is nothing to carry its Remove.
                    <Card
                      size="small"
                      title={t("portfolio.nba.title", "Next Best Actions")}
                      extra={
                        <WidgetRemoveButton
                          title={t("portfolio.nba.title", "Next Best Actions")}
                          onRemove={() => handleHide(NBA_FEED_SLUG)}
                        />
                      }
                    >
                      <Typography.Text type="secondary">
                        {t("portfolio.nba.emptyEditing")}
                      </Typography.Text>
                    </Card>
                  )}
                </div>
              )}
              {sideColumnFooter}
            </div>
          )}
        </div>
      </div>

      {engagementHost.drillModal}

      <AddWidgetsModal
        open={layout.isEditing && addTileOpen}
        entries={catalogEntries}
        onAdd={handleAddMany}
        onClose={() => setAddTileOpen(false)}
      />

      {/* Two exits from a dirty draft, one question. The route blocker is a
          component of its own because `useBlocker` throws outside a data
          router, and this component is mounted under a plain router by the
          Portfolio fallback and by a dozen specs. */}
      {inDataRouter && (
        <RouteDraftBlocker
          active={layout.isEditing && layout.isDirty}
          saving={layout.saving}
          onDiscard={layout.cancel}
          onSave={handleSave}
        />
      )}

      <UnsavedDraftDialog
        open={pendingViewId !== null}
        saving={layout.saving}
        onKeepEditing={() => setPendingViewId(null)}
        onDiscard={discardDraftForSwitch}
        onSave={saveDraftForSwitch}
      />

      {boardListOrder.length > 0 && (
        <div className="flex flex-col gap-4 min-w-0">
          {boardListOrder.map((slug) => (
            <BoardWidgetCard
              key={slug}
              slug={slug}
              adapted={boardAdapted(slug)}
              currency={currency}
              isEditing={layout.isEditing}
              refreshing={isRefreshing(slug)}
              onHide={handleHide}
              onRefresh={() => handleWidgetRefresh(slug)}
              onExpand={() => setExpandedSlug(slug)}
            />
          ))}
        </div>
      )}

      {feedAlertOrder.length > 0 && (
        <div className="flex flex-col gap-4 min-w-0">
          {feedAlertOrder.map((slug) => (
            <BoardWidgetCard
              key={slug}
              slug={slug}
              adapted={boardAdapted(slug)}
              currency={currency}
              isEditing={layout.isEditing}
              refreshing={isRefreshing(slug)}
              onHide={handleHide}
              onRefresh={() => handleWidgetRefresh(slug)}
              onExpand={() => setExpandedSlug(slug)}
            />
          ))}
        </div>
      )}

      <WidgetExpandModal
        widget={expandedAdapted?.widget ?? null}
        payload={(expandedAdapted?.payload as WidgetPayload) ?? null}
        currency={currency}
        onClose={() => setExpandedSlug(null)}
      />
    </Flex>
  )
}
